/**
 * Reply API functions
 * Uses publicAxios for unauthenticated requests
 * Authenticated requests require passing the axios instance from useAuthenticatedAxios
 */

import publicAxios from './publicAxios';

/**
 * Get replies with optional filters (public)
 * @param {object} filters - Optional filters { postId, parentId }
 */
export const getReplies = async (filters = {}) => {
    const params = new URLSearchParams();
    if (filters.postId) params.append('postId', filters.postId);
    if (filters.parentId !== undefined) {
        params.append('parentId', filters.parentId || 'null');
    }

    const response = await publicAxios.get('/api/replies', { params });
    return response.data;
};

/**
 * Get a single reply by ID (public)
 * @param {string} id - Reply ID
 */
export const getReply = async (id) => {
    const response = await publicAxios.get(
        `/api/replies/${encodeURIComponent(id)}`
    );
    return response.data;
};

/**
 * Create a new reply (authenticated)
 * @param {object} axiosInstance - Authenticated axios instance
 * @param {object} replyData - Reply data { postId, parentId, content }
 */
export const createReply = async (axiosInstance, replyData) => {
    const response = await axiosInstance.post('/api/replies', replyData);
    return response.data;
};

/**
 * Update a reply (authenticated)
 * @param {object} axiosInstance - Authenticated axios instance
 * @param {string} id - Reply ID
 * @param {object} updates - Fields to update
 */
export const updateReply = async (axiosInstance, id, updates) => {
    const response = await axiosInstance.put(`/api/replies/${id}`, updates);
    return response.data;
};

/**
 * Delete a reply (authenticated)
 * @param {object} axiosInstance - Authenticated axios instance
 * @param {string} id - Reply ID
 */
export const deleteReply = async (axiosInstance, id) => {
    const response = await axiosInstance.delete(`/api/replies/${id}`);
    return response.data;
};

/**
 * Report a reply (authenticated)
 * @param {object} axiosInstance - Authenticated axios instance
 * @param {string} id - Reply ID
 */
export const reportReply = async (
    axiosInstance,
    id,
    { reason, comments } = {}
) => {
    const response = await axiosInstance.post(`/api/replies/${id}/report`, {
        reason,
        comments,
    });
    return response.data;
};

/**
 * Check if current user has engaged with a reply (authenticated)
 * @param {object} axiosInstance - Authenticated axios instance
 * @param {string} replyId - Reply ID
 */
export const checkReplyEngagement = async (axiosInstance, replyId) => {
    const response = await axiosInstance.get(
        `/api/reply-engagements/check/${replyId}`
    );
    return response.data;
};

/**
 * Like a reply (authenticated)
 * @param {object} axiosInstance - Authenticated axios instance
 * @param {string} replyId - Reply ID
 */
export const likeReply = async (axiosInstance, replyId) => {
    const response = await axiosInstance.post(
        '/api/reply-engagements/reaction',
        {
            replyId,
            reaction: 'like',
        }
    );
    return response.data;
};

/**
 * Dislike a reply (authenticated)
 * @param {object} axiosInstance - Authenticated axios instance
 * @param {string} replyId - Reply ID
 */
export const dislikeReply = async (axiosInstance, replyId) => {
    const response = await axiosInstance.post(
        '/api/reply-engagements/reaction',
        {
            replyId,
            reaction: 'dislike',
        }
    );
    return response.data;
};
